import React, { useState, useEffect, useCallback } from 'react';
import { generateSubdomainFromAppName } from '../../utils/customDomain';

/**
 * ConfigureStep - Subdomain configuration with validation
 * Allows user to choose subdomain and optionally manage custom domains
 */

interface ConfigureStepProps {
  appName?: string;
  initialSubdomain?: string;
  onPublish: (subdomain: string) => void;
  onManageDomains: () => void;
  onValidateSubdomain: (subdomain: string) => Promise<boolean>;
  isPublishing?: boolean;
}

const ConfigureStep: React.FC<ConfigureStepProps> = ({
  appName,
  initialSubdomain,
  onPublish,
  onManageDomains,
  onValidateSubdomain,
  isPublishing,
}) => {
  // Generate initial subdomain suggestion from app name
  const generateSlug = (name?: string): string => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-')
      .substring(0, 32);
  };

  const [subdomain, setSubdomain] = useState(initialSubdomain || generateSubdomainFromAppName(appName));
  const [isValidating, setIsValidating] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [showRules, setShowRules] = useState(false);
  const [showCustomDomain, setShowCustomDomain] = useState(false);

  // Debounced validation
  useEffect(() => {
    if (!subdomain) {
      setIsAvailable(null);
      setValidationError('');
      return;
    }

    // Client-side validation
    const errors = validateSubdomainFormat(subdomain);
    if (errors) {
      setIsAvailable(false);
      setValidationError(errors);
      return;
    }

    // Server-side validation with debounce
    const timer = setTimeout(async () => {
      setIsValidating(true);
      setValidationError('');
      try {
        const available = await onValidateSubdomain(subdomain);
        setIsAvailable(available);
        if (!available) {
          setValidationError('This subdomain is already taken');
        }
      } catch (error) {
        setValidationError('Could not validate subdomain. Please try again.');
        setIsAvailable(false);
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [subdomain, onValidateSubdomain]);

  const validateSubdomainFormat = (value: string): string => {
    if (value.length < 3) return 'Minimum 3 characters required';
    if (value.length > 20) return 'Maximum 20 characters allowed';
    if (!/^[a-z0-9-]+$/.test(value)) return 'Only lowercase letters, numbers, and hyphens allowed';
    if (/^-|-$/.test(value)) return 'Cannot start or end with a hyphen';
    if (/--/.test(value)) return 'Cannot contain consecutive hyphens';
    return '';
  };

  const handlePublish = useCallback(() => {
    if (isAvailable && subdomain && !isPublishing) {
      onPublish(subdomain);
    }
  }, [isAvailable, subdomain, isPublishing, onPublish]);

  const canPublish = isAvailable === true && !isValidating && !isPublishing;

  return (
    <div className="px-6 py-4">
      {/* Title */}
      <h2 className="text-2xl font-bold text-white mb-2">Configure Your Web Hosting</h2>
      <p className="text-white/70 text-sm mb-4">
        Your app will be available at this URL. You can add a custom domain later.
      </p>

      {/* Subdomain Input Section */}
      <div className="mb-4">
        <label className="block text-white/90 text-sm font-medium mb-2">
          Subdomain
        </label>
        
        <div className="relative">
          <input
            type="text"
            value={subdomain}
            onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
            disabled={isPublishing}
            placeholder="myapp"
            className={`w-full bg-[#0f172a]/60 border rounded-lg text-white px-4 py-3 pr-32 outline-none transition-all duration-200 ${
              isValidating
                ? 'border-white/20'
                : isAvailable === true
                ? 'border-[#22c55e]/50 focus:border-[#22c55e] focus:shadow-[0_0_0_3px_rgba(34,197,94,0.1)]'
                : isAvailable === false
                ? 'border-red-500/50 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.1)]'
                : 'border-white/10 focus:border-white/30 focus:shadow-[0_0_0_3px_rgba(255,255,255,0.05)]'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          />
          
          {/* Suffix */}
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 text-sm pointer-events-none">
            .b4a.app
          </div>

          {/* Validation Icon */}
          {!isValidating && isAvailable !== null && (
            <div className="absolute right-24 top-1/2 -translate-y-1/2">
              {isAvailable ? (
                <svg className="w-5 h-5 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
          )}

          {/* Loading Spinner */}
          {isValidating && (
            <div className="absolute right-24 top-1/2 -translate-y-1/2">
              <div className="w-5 h-5 border-2 border-white/20 border-t-[#22c55e] rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Validation Feedback */}
        {isAvailable === true && !isValidating && (
          <p className="text-[#22c55e] text-sm mt-2 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            This subdomain is available
          </p>
        )}
        {validationError && (
          <p className="text-red-400 text-sm mt-2 flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {validationError}
          </p>
        )}
      </div>

      {/* Subdomain Rules (Collapsible) */}
      <div className="mb-4">
        <button
          onClick={() => setShowRules(!showRules)}
          className="flex items-center gap-2 text-white/70 text-sm hover:text-white transition-colors duration-200"
        >
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${showRules ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Subdomain Rules
        </button>

        {showRules && (
          <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-3">
            <ul className="space-y-2 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <span className="text-white/40">•</span>
                Only lowercase letters, numbers, and hyphens
              </li>
              <li className="flex items-start gap-2">
                <span className="text-white/40">•</span>
                Minimum 3 characters
              </li>
              <li className="flex items-start gap-2">
                <span className="text-white/40">•</span>
                Cannot start or end with a hyphen
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Custom Domain Section (Collapsible) */}
      <div className="mb-4">
        <button
          onClick={() => setShowCustomDomain(!showCustomDomain)}
          className="flex items-center gap-2 text-white/70 text-sm hover:text-white transition-colors duration-200"
        >
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${showCustomDomain ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Connect a custom domain (advanced)
        </button>

        {showCustomDomain && (
          <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-3">
            <p className="text-sm text-white/70">
              You can add and manage custom domains after publishing your app.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={handlePublish}
          disabled={!canPublish}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-semibold text-sm border border-[#22c55e]/30 shadow-lg shadow-[#22c55e]/20 hover:from-[#16a34a] hover:to-[#15803d] hover:shadow-[#22c55e]/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
        >
          {isPublishing ? 'Publishing...' : 'Publish Now'}
        </button>
      </div>
    </div>
  );
};

export default ConfigureStep;

